import React, { useState, useEffect, useMemo } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import axiosInstance from '../../interceptor/axiosInstance';
import { useNavigate } from 'react-router-dom';
import './ClientsList.css';

const fmt = (iso) =>
  iso
    ? new Date(iso).toLocaleString(undefined, {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    : '—';



function AnswerRow({ row, isClient }) {
  const navigate = useNavigate();
  const initial = isClient
    ? row.question[0].toUpperCase()
    : `C`;
  const threadId = row.thread_ids?.[0];

  return (
    <div className="ca-row">
  <div className={`ca-avatar ${isClient ? 'ca-avatar--question' : ''}`}>{initial}</div>
  <div className="ca-row-main">
    <div className="ca-row-label">{isClient ? row.question : `Client #${row.client_id}`}</div>
    <div className="ca-row-sub">{fmt(row.submitted_at)}</div>
  </div>
  <div>
  <div className="ca-field-label ca-field-label--answer">
    Answer
  </div>

  <div className="ca-answer-pill">
    {row.answer}
  </div>
</div>
  {!isClient && threadId && (
    <button
      type="button"
      className="ms-auto"
      style={{fontSize:"10px", borderRadius:"10px", borderColor:"#bb0403", maxWidth:"100px", maxHeight:"25px"}}
      onClick={() => navigate(`/chat?thread_id=${threadId}`)}
      title="Go to chat"
    >
      Go to Chat
    </button>
  )}
</div>
  );
}
function Card({ item, isClient }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const firstThreadId = isClient
    ? item.rows.find(r => r.thread_ids?.length)?.thread_ids?.[0]
    : null;

  return (
    <div className={`ca-card ${open ? 'ca-card--open' : ''}`}>
      <button
        type="button"
        className="ca-card-header"
        onClick={() => setOpen(v => !v)}
      >
        <div className="ca-card-header-left">
          <div>
            <div className="ca-card-title">{item.title}</div>
            <div className="ca-card-sub">
              {item.rows.length}{' '}
              {item.rows.length === 1
                ? isClient
                  ? 'answer'
                  : 'response'
                : isClient
                ? 'answers'
                : 'responses'}
            </div>
          </div>
        </div>

        <div className="ca-card-header-right">
          {isClient && firstThreadId && (
            <button
              type="button"
              style={{fontSize:"10px", borderRadius:"10px", borderColor:"#bb0403", maxWidth:"100px", maxHeight:"25px"}}
              onClick={e => { e.stopPropagation(); navigate(`/chat?thread_id=${firstThreadId}`); }}
              title="Go to chat"
            >
              Go to Chat
            </button>
          )}
          <span className="ca-badge">{item.rows.length}</span>
          <span className={`ca-chevron ${open ? 'ca-chevron--up' : ''}`}>
            &#8964;
          </span>
        </div>
      </button>

      {open && (
        <div className="ca-card-body">
          {item.rows.length === 0 ? (
            <div className="ca-empty">
              No responses.
            </div>
          ) : (
            item.rows.map((row, i) => (
              <AnswerRow
                key={`${row.client_id ?? row.question}-${i}`}
                row={row}
                isClient={isClient}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default function ChatbotAnswers() {
  const [data, setData]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [view, setView]       = useState('questions'); // 'questions' | 'clients'
  const [search, setSearch]   = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data: res } = await axiosInstance.get('/chatbot-questions/answers');
        setData(Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : []);
      } catch (e) {
        setError('Failed to load responses. Please try again.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const byQuestions = useMemo(() =>
    data.map(q => ({
      key: q.question_snapshot,
      title: q.question_snapshot,
      rows: q.answers,
    })),
  [data]);

  const byClients = useMemo(() => {
    const map = new Map();
    data.forEach(q => {
      q.answers.forEach(a => {
        if (!map.has(a.client_id)) map.set(a.client_id, []);
        map.get(a.client_id).push({
          question: q.question_snapshot,
          answer: a.answer,
          submitted_at: a.submitted_at,
          client_id: a.client_id,
          thread_ids: a.thread_ids,
        }); 
      });
    });
    return Array.from(map.entries())
      .sort((a, b) => {
        const aLatest = Math.max(...a[1].map(r => new Date(r.submitted_at)));
        const bLatest = Math.max(...b[1].map(r => new Date(r.submitted_at)));
        return bLatest - aLatest;
      })
      .map(([cid, rows]) => ({
        key: String(cid),
        title: `Client #${cid}`,
        rows,
      }));
  }, [data]);

  const items = view === 'questions' ? byQuestions : byClients;

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return items;
    return items
      .map(item => ({
        ...item,
        rows: item.rows.filter(r =>
          (r.answer?.toLowerCase().includes(q)) ||
          (r.question?.toLowerCase().includes(q)) ||
          item.title.toLowerCase().includes(q)
        ),
      }))
      .filter(item => item.title.toLowerCase().includes(q) || item.rows.length > 0);
  }, [items, search]);

  const totalQuestions  = data.length;
  const totalResponses  = data.reduce((s, q) => s + q.answers.length, 0);
  const totalClients    = new Set(data.flatMap(q => q.answers.map(a => a.client_id))).size;

  return (
    <div className="mt-4 agent-container">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0 heading-large">Question Responses</h3>

      </div>

      {!loading && !error && (
    
  <div className="ca-summary">
  <div className="ca-summary-item">
    <span className="ca-summary-num">{totalQuestions}</span>
    <span className="ca-summary-lbl">Questions</span>
  </div>
  <div className="ca-summary-divider" />
  <div className="ca-summary-item">
    <span className="ca-summary-num">{totalResponses}</span>
    <span className="ca-summary-lbl">Responses</span>
  </div>
  <div className="ca-summary-divider" />
  <div className="ca-summary-item">
    <span className="ca-summary-num">{totalClients}</span>
    <span className="ca-summary-lbl">Clients</span>
  </div>
</div>

      )}

{!loading && !error && (
  <div className="ca-toolbar">
    <input
      type="text"
      className="ca-search-bar"
      placeholder="Search questions, clients or answers..."
      value={search}
      onChange={e => setSearch(e.target.value)}
    />

    <div className="ca-view-tabs">
      {[['questions', 'By Question'], ['clients', 'By Client']].map(
        ([val, label]) => (
          <button
            key={val}
            type="button"
            className={`ca-view-tab ${
              view === val ? 'ca-view-tab--active' : ''
            }`}
            onClick={() => {
              setView(val);
              setSearch('');
            }}
          >
            {label}
          </button>
        )
      )}
    </div>
  </div>
)}

      {loading && (
        <div className="text-center py-5 text-muted">Loading responses…</div>
      )}

      {error && (
        <div className="alert alert-danger">{error}</div>
      )}

      {!loading && !error && (
        <div>
          {filtered.length === 0 ? (
            <div className="text-center py-5 text-muted">No results found.</div>
          ) : (
            filtered.map(item => (
              <Card
                key={`${view}-${item.key}`}
                item={item}
                isClient={view === 'clients'}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}